import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TOTAL_QUESTIONS, StoredQuestion, OpenTriviaResponse } from '@shared/protocol';
import { getApiUrl } from '../config';

// Decode HTML entities
function decodeHtmlEntities(text: string): string {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.documentElement.textContent || text;
}

// Shuffle array
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Translate text using MyMemory API
async function translateToSpanish(text: string): Promise<string> {
  try {
    const response = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|es`
    );
    const data = await response.json() as { responseData?: { translatedText?: string } };
    return data.responseData?.translatedText || text;
  } catch {
    return text; // Fallback to original if translation fails
  }
}

// Translate a question and its answers
async function translateQuestion(question: StoredQuestion): Promise<StoredQuestion> {
  const [translatedQ, ...translatedAnswers] = await Promise.all([
    translateToSpanish(question.question),
    ...question.answers.map(a => translateToSpanish(a))
  ]);
  
  return {
    ...question,
    question: translatedQ,
    answers: translatedAnswers
  };
}

// Fetch questions from Open Trivia DB
async function fetchQuestions(): Promise<StoredQuestion[]> {
  const response = await fetch(`https://opentdb.com/api.php?amount=${TOTAL_QUESTIONS}&type=multiple`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch questions');
  }
  
  const data = await response.json() as OpenTriviaResponse;
  
  if (data.response_code !== 0 || !data.results?.length) {
    throw new Error('No questions returned');
  }
  
  return data.results.map(q => {
    const correctAnswer = decodeHtmlEntities(q.correct_answer);
    const allAnswers = [
      correctAnswer,
      ...q.incorrect_answers.map(decodeHtmlEntities),
    ];
    const shuffledAnswers = shuffleArray(allAnswers);
    const correctIndex = shuffledAnswers.indexOf(correctAnswer);
    
    return {
      question: decodeHtmlEntities(q.question),
      answers: shuffledAnswers,
      correctIndex,
    };
  });
}

function HomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>('en');

  const createSession = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Step 1: Fetch questions
      setLoadingStatus('Fetching questions...');
      const questions = await fetchQuestions();
      
      // Step 2: Translate if Spanish selected
      let finalQuestions = questions;
      if (language === 'es') {
        setLoadingStatus('Translating to Spanish...');
        finalQuestions = await Promise.all(questions.map(translateQuestion));
      }
      
      // Step 3: Create session
      setLoadingStatus('Creating game...');
      const response = await fetch(getApiUrl('/api/session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ questions: finalQuestions, language }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create session');
      }
      
      const data: { sessionId: string } = await response.json();
      // Navigate to the lobby page with the session ID
      navigate(`/lobby/${data.sessionId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div className="page page-centered">
      <div className="container container-narrow text-center">
        <div className="header">
          <div className="logo">🧠</div>
          <h1>Family Trivia</h1>
          <p className="subtitle">Speed Run Challenge</p>
        </div>
        
        <div className="card mt-3">
          <h2>How to Play</h2>
          <ul className="how-to-list">
            <li>🎮 Create a game session</li>
            <li>📱 Players scan QR code to join</li>
            <li>🚀 First player starts the game</li>
            <li>⚡ Race through {TOTAL_QUESTIONS} trivia questions</li>
            <li>🏆 Fastest time wins!</li>
          </ul>
        </div>
        
        {/* Language Toggle */}
        <div className="language-toggle mt-3">
          <span className={language === 'en' ? 'active' : ''}>🇺🇸 English</span>
          <button 
            className={`toggle-switch ${language === 'es' ? 'active' : ''}`}
            onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
            disabled={loading}
            aria-label="Toggle language"
          >
            <span className="toggle-slider" />
          </button>
          <span className={language === 'es' ? 'active' : ''}>🇪🇸 Español</span>
        </div>
        
        {error && (
          <div className="error-message mt-2">
            {error}
          </div>
        )}
        
        <button 
          className="btn btn-primary btn-large btn-full mt-3"
          onClick={createSession}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="waiting-spinner small" />
              {loadingStatus}
            </>
          ) : (
            '🚀 Create Game'
          )}
        </button>
      </div>
    </div>
  );
}

export default HomePage;
